import React, { useRef, useState, useCallback, useEffect } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useLanguage } from "../../../contexts/LanguageContext";
import SectionHeading from "../SectionHeading";

/** Images en haute résolution (2x) pour écrans retina et netteté */
const DISHES = [
  {
    nameKey: "topDish1Name",
    descKey: "topDish1Desc",
    rating: 4.8,
    img: "https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=800&h=1000&fit=crop&q=85",
  },
  {
    nameKey: "topDish2Name",
    descKey: "topDish2Desc",
    rating: 5.0,
    img: "https://images.unsplash.com/photo-1540189549336-e6e99c3679fe?w=800&h=1000&fit=crop&q=85",
  },
  {
    nameKey: "topDish3Name",
    descKey: "topDish3Desc",
    rating: 4.6,
    img: "https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?w=800&h=1000&fit=crop&q=85",
  },
  {
    nameKey: "topDish4Name",
    descKey: "topDish4Desc",
    rating: 4.9,
    img: "https://images.unsplash.com/photo-1476124369491-e7addf5db371?w=800&h=1000&fit=crop&q=85",
  },
  {
    nameKey: "topDish5Name",
    descKey: "topDish5Desc",
    rating: 4.7,
    img: "https://images.unsplash.com/photo-1565958011703-44f9829ba187?w=800&h=1000&fit=crop&q=85",
  },
];

/** 3 copies pour boucle continue sans retour visuel */
const EXTENDED_DISHES = [...DISHES, ...DISHES, ...DISHES];
const N = DISHES.length;
const AUTO_PLAY_INTERVAL = 4500;

const cardVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      delay: (i % N) * 0.06,
      ease: [0.22, 1, 0.36, 1],
    },
  }),
};

const TopListSection = ({ onViewRecipes }) => {
  const { t } = useLanguage();
  const shouldReduceMotion = useReducedMotion();
  const scrollRef = useRef(null);
  const sectionRef = useRef(null);
  const isJumpingRef = useRef(false);
  const scrollRafRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isInView, setIsInView] = useState(true);

  const getLoopMetrics = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return null;
    const cards = el.querySelectorAll("[data-card]");
    if (cards.length < N * 3) return null;
    const middleStart = cards[N].offsetLeft;
    const middleEnd = cards[N * 2].offsetLeft;
    const loopWidth = middleEnd - middleStart;
    return { el, cards, middleStart, middleEnd, loopWidth };
  }, []);

  /**
   * Repositionne le scroll instantanément, sans animation.
   * Le conteneur a `scroll-behavior: smooth` (classe `scroll-smooth`) pour la
   * navigation utilisateur : sans ce contournement, cette affectation serait
   * elle aussi animée par le navigateur, et les valeurs intermédiaires de
   * l'animation retriggeraient la correction de boucle de `handleScroll`
   * (scrollLeft encore < middleStart pendant la montée), provoquant un
   * emballement du scroll au montage.
   */
  const jumpScrollLeft = useCallback((el, value) => {
    const previous = el.style.scrollBehavior;
    el.style.scrollBehavior = "auto";
    el.scrollLeft = value;
    el.style.scrollBehavior = previous;
  }, []);

  /** Position initiale : milieu (2e copie) pour pouvoir boucler dans les deux sens */
  useEffect(() => {
    const init = () => {
      const metrics = getLoopMetrics();
      if (!metrics) return;
      jumpScrollLeft(metrics.el, metrics.middleStart);
    };
    const id = requestAnimationFrame(init);
    return () => cancelAnimationFrame(id);
  }, [getLoopMetrics, jumpScrollLeft]);

  /** Pause du défilement auto quand la section est hors de l'écran */
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsInView(entry.isIntersecting),
      { threshold: 0.2, rootMargin: "0px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  /** Détermine l’index physique (0 à EXTENDED_DISHES.length - 1) le plus visible */
  const getRawIndexFromScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return N;
    const scrollLeft = el.scrollLeft;
    const cards = el.querySelectorAll("[data-card]");
    let best = N;
    let bestDist = Infinity;
    cards.forEach((card, i) => {
      const dist = Math.abs(scrollLeft - card.offsetLeft);
      if (dist < bestDist) {
        bestDist = dist;
        best = i;
      }
    });
    return best;
  }, []);

  const handleScroll = useCallback(() => {
    if (scrollRafRef.current) return;
    scrollRafRef.current = requestAnimationFrame(() => {
      scrollRafRef.current = null;

      const metrics = getLoopMetrics();
      if (!metrics || isJumpingRef.current) return;
      const { el, middleStart, middleEnd, loopWidth } = metrics;
      const scrollLeft = el.scrollLeft;

      /** Replacer dans la copie du milieu pour boucle continue */
      if (scrollLeft >= middleEnd) {
        isJumpingRef.current = true;
        jumpScrollLeft(el, scrollLeft - loopWidth);
        requestAnimationFrame(() => {
          isJumpingRef.current = false;
        });
      } else if (scrollLeft < middleStart) {
        isJumpingRef.current = true;
        jumpScrollLeft(el, scrollLeft + loopWidth);
        requestAnimationFrame(() => {
          isJumpingRef.current = false;
        });
      }

      const rawIndex = getRawIndexFromScroll();
      const normalizedIndex = rawIndex % N;
      setCurrentIndex((prev) =>
        prev === normalizedIndex ? prev : normalizedIndex,
      );
    });
  }, [getLoopMetrics, getRawIndexFromScroll, jumpScrollLeft]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      el.removeEventListener("scroll", handleScroll);
      if (scrollRafRef.current) {
        cancelAnimationFrame(scrollRafRef.current);
        scrollRafRef.current = null;
      }
    };
  }, [handleScroll]);

  /** Scroll vers la carte logique (0 à N-1) dans la copie du milieu */
  const goToIndex = useCallback(
    (logicalIndex) => {
      const metrics = getLoopMetrics();
      if (!metrics) return;
      const { el, cards } = metrics;
      const safeIndex = ((logicalIndex % N) + N) % N;
      const middleIndex = N + safeIndex;
      const card = cards[middleIndex];
      if (!card) return;
      el.scrollTo({
        left: card.offsetLeft,
        behavior: shouldReduceMotion ? "auto" : "smooth",
      });
      setCurrentIndex(safeIndex);
    },
    [getLoopMetrics, shouldReduceMotion],
  );

  const goPrev = useCallback(() => {
    const prev = (currentIndex - 1 + N) % N;
    goToIndex(prev);
  }, [currentIndex, goToIndex]);

  const goNext = useCallback(() => {
    const next = (currentIndex + 1) % N;
    goToIndex(next);
  }, [currentIndex, goToIndex]);

  /** Défilement automatique */
  useEffect(() => {
    if (isPaused || !isInView) return;
    const timer = setInterval(() => {
      goNext();
    }, AUTO_PLAY_INTERVAL);
    return () => clearInterval(timer);
  }, [isPaused, isInView, goNext]);

  const handleKeyDown = (e) => {
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      goPrev();
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      goNext();
    }
  };

  return (
    <section
      ref={sectionRef}
      className="py-8 sm:py-12 md:py-16 w-full min-w-0 overflow-x-hidden"
      aria-label={t("topRecipesTitle")}
    >
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
        <SectionHeading
          index="02"
          kicker={t("worldMenus")}
          title={t("topRecipesTitle")}
          description={t("topRecipesSubtitle")}
          className="mb-0 flex-1"
        />

        {/* Contrôles + compteur, alignés avec le titre sur desktop */}
        <div className="hidden sm:flex items-center gap-3 shrink-0 pb-1">
          <button
            type="button"
            onClick={goPrev}
            aria-label={t("topRecipesPrevAria")}
            className="flex items-center justify-center w-11 h-11 rounded-full border border-ink-900/15 text-ink-900 hover:border-ember-600 hover:text-ember-600 active:scale-95 transition-colors duration-150"
          >
            ←
          </button>
          <button
            type="button"
            onClick={goNext}
            aria-label={t("topRecipesNextAria")}
            className="flex items-center justify-center w-11 h-11 rounded-full border border-ink-900/15 text-ink-900 hover:border-ember-600 hover:text-ember-600 active:scale-95 transition-colors duration-150"
          >
            →
          </button>
        </div>
      </div>

      <div
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        onFocus={() => setIsPaused(true)}
        onBlur={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget)) setIsPaused(false);
        }}
      >
        <div
          ref={scrollRef}
          role="region"
          aria-roledescription="carousel"
          aria-label={t("topRecipesCarouselAria")}
          tabIndex={0}
          onKeyDown={handleKeyDown}
          className={`overflow-x-auto overflow-y-hidden overscroll-x-none snap-x snap-mandatory scrollbar-hide focus:outline-none focus-visible:ring-2 focus-visible:ring-ember-500 focus-visible:ring-offset-2 focus-visible:ring-offset-ink-50 rounded-xl ${
            shouldReduceMotion ? "" : "scroll-smooth"
          }`}
          style={{
            WebkitOverflowScrolling: "touch",
            overscrollBehaviorX: "contain",
            scrollPaddingInline: "4px",
          }}
        >
          <div className="flex gap-4 sm:gap-5 py-1 sm:py-2">
            {EXTENDED_DISHES.map((d, i) => {
              const dishName = t(d.nameKey);
              const dishDesc = t(d.descKey);
              const rank = String((i % N) + 1).padStart(2, "0");
              return (
                <motion.article
                  key={`${i}-${d.nameKey}`}
                  data-card
                  custom={i}
                  initial={false}
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={cardVariants}
                  className="group relative w-[68vw] min-w-[210px] max-w-[250px] sm:w-[250px] sm:min-w-[250px] md:w-[270px] md:min-w-[270px] lg:w-[290px] lg:min-w-[290px] flex-shrink-0 snap-center snap-always"
                  aria-label={dishName}
                >
                  <button
                    type="button"
                    onClick={onViewRecipes}
                    className="relative block w-full aspect-[3/4] rounded-xl overflow-hidden bg-ink-100 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-ember-500 focus-visible:ring-offset-2 focus-visible:ring-offset-ink-50"
                  >
                    <img
                      src={d.img}
                      alt={dishName}
                      className="w-full h-full object-cover object-center transition-transform duration-500 ease-out group-hover:scale-105"
                      loading={i < N ? (i === 0 ? "eager" : "lazy") : "lazy"}
                      decoding="async"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/10 to-transparent" />

                    <span className="absolute top-3 left-3 font-display text-sm font-semibold text-white/70">
                      {rank}
                    </span>
                    <span
                      className="absolute top-3 right-3 flex items-center gap-1 rounded-full bg-ink-950/60 backdrop-blur-sm px-2.5 py-1 text-xs font-semibold text-white"
                      aria-label={`${d.rating} ${t("topRecipesStarsAria")}`}
                    >
                      <span className="text-ember-400" aria-hidden>
                        ★
                      </span>
                      {d.rating}
                    </span>

                    <div className="absolute inset-x-0 bottom-0 p-4">
                      <h3 className="font-semibold text-white text-base mb-1 line-clamp-1">
                        {dishName}
                      </h3>
                      <p className="text-white/70 text-xs line-clamp-2">
                        {dishDesc}
                      </p>
                    </div>
                  </button>
                </motion.article>
              );
            })}
          </div>
        </div>
      </div>

      {/* Barre de progression + compteur, remplace la pagination à points générique */}
      <div className="flex items-center gap-4 mt-6 sm:mt-8">
        <span className="font-poster text-base font-bold text-ink-500 tabular-nums shrink-0">
          {String(currentIndex + 1).padStart(2, "0")} / {String(N).padStart(2, "0")}
        </span>
        <div
          className="relative flex-1 h-px bg-ink-900/15"
          role="tablist"
          aria-label={t("topRecipesPositionAria")}
        >
          <motion.div
            className="absolute top-0 left-0 h-px bg-ember-600"
            animate={{ width: `${((currentIndex + 1) / N) * 100}%` }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          />
        </div>
        <div className="flex sm:hidden items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={goPrev}
            aria-label={t("topRecipesPrevAria")}
            className="flex items-center justify-center w-9 h-9 rounded-full border border-ink-900/15 text-ink-900 active:scale-95"
          >
            ←
          </button>
          <button
            type="button"
            onClick={goNext}
            aria-label={t("topRecipesNextAria")}
            className="flex items-center justify-center w-9 h-9 rounded-full border border-ink-900/15 text-ink-900 active:scale-95"
          >
            →
          </button>
        </div>
      </div>
    </section>
  );
};

export default TopListSection;
